import { resolvePlatformAppByWebPath } from "./platformAppCatalog.js";
import { isMobilePlanningWebOnlyPath } from "../trainerstunden/mobile-session-detail.js";

const TRAINER_DRILL_DETAIL_RE = /^\/(?:hub\/)?trainer\/drills\/([^/?#]+)$/;
const TRAINER_DRILLS_LIST_RE = /^\/(?:hub\/)?trainer(?:\/drills)?$/;
const TRAINER_LESSON_PLANS_LIST_RE = /^\/(?:hub\/)?trainer\/lesson-plans$/;
const TRAINER_LESSON_PLAN_NEW_RE = /^\/(?:hub\/)?trainer\/lesson-plans\/new$/;
const TRAINER_LESSON_PLAN_EDIT_RE =
  /^\/(?:hub\/)?trainer\/lesson-plans\/([^/?#]+)\/edit$/;
const TRAINER_LESSON_PLAN_DETAIL_RE =
  /^\/(?:hub\/)?trainer\/lesson-plans\/([^/?#]+)$/;
const TRAININGS_DESK_BILLING_RE = /^\/(?:hub\/)?trainingsdesk\/rechnungen$/;
const TRAININGS_DESK_ABRECHNEN_RE =
  /^\/(?:hub\/)?trainingsdesk\/(?:abrechnen|offenePosten)$/;
const TRAININGS_DESK_ZAHLUNGEN_RE = /^\/(?:hub\/)?trainingsdesk\/zahlungen$/;
const TRAININGS_DESK_PLANUNG_RE = /^\/(?:hub\/)?trainingsdesk\/planung$/;
const TRAININGS_DESK_TERMINE_RE = /^\/(?:hub\/)?trainingsdesk\/termine$/;
const TRAININGS_DESK_FAMILY_ROOT_RE = /^\/(?:hub\/)?trainingsdesk\/family$/;
const MINIGAMES_BUBBLE_TAP_RE = /^\/(?:hub\/)?minigames-bubble-tap$/;
const MEDENSPIELE_TRAINING_RE =
  /^\/(?:hub\/)?medenspiele\/(?:training|planung|planning|freies-training)$/;
const CLUBHUB_ACCESS_REQUESTS_ADMIN_RE = /^\/(?:hub\/)?access-requests-admin$/;

const NATIVE_BILLING_LIST_QUERY_KEYS = new Set(["archive", "archiveMonth"]);

function __isNativeBillingListQuery(query) {
  const keys = [...query.keys()];
  if (keys.length === 0) return true;
  return keys.every((key) => NATIVE_BILLING_LIST_QUERY_KEYS.has(key));
}

function __normalizePathname(webPath) {
  const raw = String(webPath || "").trim();
  const withoutQuery = raw.split("?")[0].split("#")[0];
  return `/${withoutQuery.replace(/^\/+/, "").replace(/\/+$/, "")}`;
}

function __readQuery(webPath) {
  const raw = String(webPath || "").trim();
  const qIdx = raw.indexOf("?");
  if (qIdx < 0) return new URLSearchParams();
  return new URLSearchParams(raw.slice(qIdx + 1));
}

/**
 * @param {string} webPath
 * @returns {{ governanceKey: string, params?: Record<string, unknown> } | null}
 */
function __resolveTrainingsDeskPlanung(webPath) {
  const pathname = __normalizePathname(webPath);
  if (!TRAININGS_DESK_PLANUNG_RE.test(pathname)) return null;

  if (isMobilePlanningWebOnlyPath(webPath)) return null;

  const query = __readQuery(webPath);
  const sessionId = String(query.get("session") || "").trim();
  if (sessionId) {
    return {
      governanceKey: "trainerstunden",
      params: {
        nativeStackScreen: "TrainingsDeskSessionDetail",
        sessionId,
      },
    };
  }

  if (String(query.get("newSession") || "").trim() === "1") {
    return {
      governanceKey: "trainerstunden",
      params: { nativeStackScreen: "TrainingsDeskSessionCreate" },
    };
  }

  const filter = String(query.get("filter") || "")
    .trim()
    .toLowerCase();
  if (filter === "makeup-backlog") {
    return {
      governanceKey: "trainerstunden",
      params: { initialTab: "backlog" },
    };
  }

  return {
    governanceKey: "trainerstunden",
    params: { initialTab: "agenda" },
  };
}

/**
 * Nur App-Root-Pfade (nicht Unterseiten) → native Navigation in iOS.
 *
 * @param {string} webPath
 * @returns {{ governanceKey: string, params?: Record<string, unknown> } | null}
 */
export function resolveNativeNavigationForWebPath(webPath) {
  const normalized = __normalizePathname(webPath);
  if (!normalized || normalized === "/") return null;

  const drillDetail = TRAINER_DRILL_DETAIL_RE.exec(normalized);
  if (drillDetail) {
    return {
      governanceKey: "trainer",
      params: { drillId: decodeURIComponent(drillDetail[1]) },
    };
  }

  if (TRAINER_DRILLS_LIST_RE.test(normalized)) {
    return { governanceKey: "trainer" };
  }

  if (TRAINER_LESSON_PLANS_LIST_RE.test(normalized)) {
    return {
      governanceKey: "trainer",
      params: { nativeStackScreen: "LessonPlansList" },
    };
  }

  if (TRAINER_LESSON_PLAN_NEW_RE.test(normalized)) {
    return {
      governanceKey: "trainer",
      params: { nativeStackScreen: "LessonPlanEdit" },
    };
  }

  const lessonPlanEdit = TRAINER_LESSON_PLAN_EDIT_RE.exec(normalized);
  if (lessonPlanEdit) {
    return {
      governanceKey: "trainer",
      params: {
        nativeStackScreen: "LessonPlanEdit",
        planId: decodeURIComponent(lessonPlanEdit[1]),
      },
    };
  }

  const lessonPlanDetail = TRAINER_LESSON_PLAN_DETAIL_RE.exec(normalized);
  if (lessonPlanDetail) {
    return {
      governanceKey: "trainer",
      params: {
        nativeStackScreen: "LessonPlanDetail",
        planId: decodeURIComponent(lessonPlanDetail[1]),
      },
    };
  }

  if (TRAININGS_DESK_ABRECHNEN_RE.test(normalized)) {
    return {
      governanceKey: "trainerstunden",
      params: { nativeStackScreen: "TrainingsDeskBilling" },
    };
  }

  if (TRAININGS_DESK_ZAHLUNGEN_RE.test(normalized)) {
    const query = __readQuery(webPath);
    const stmt = String(query.get("stmt") || "").trim();
    if (stmt) {
      return {
        governanceKey: "trainerstunden",
        params: {
          nativeStackScreen: "TrainingsDeskBillingStatement",
          statementId: stmt,
        },
      };
    }
    return {
      governanceKey: "trainerstunden",
      params: { nativeStackScreen: "TrainingsDeskPayments" },
    };
  }

  if (TRAININGS_DESK_BILLING_RE.test(normalized)) {
    const query = __readQuery(webPath);
    const stmt = String(query.get("stmt") || "").trim();
    if (stmt) {
      return {
        governanceKey: "trainerstunden",
        params: {
          nativeStackScreen: "TrainingsDeskBillingStatement",
          statementId: stmt,
        },
      };
    }
    if (!__isNativeBillingListQuery(query)) return null;
    return {
      governanceKey: "trainerstunden",
      params: { nativeStackScreen: "TrainingsDeskBilling" },
    };
  }

  if (TRAININGS_DESK_TERMINE_RE.test(normalized)) {
    return {
      governanceKey: "trainerstunden",
      params: { initialTab: "agenda" },
    };
  }

  if (TRAININGS_DESK_FAMILY_ROOT_RE.test(normalized)) {
    return { governanceKey: "trainerstunden" };
  }

  if (MINIGAMES_BUBBLE_TAP_RE.test(normalized)) {
    return {
      governanceKey: "minigames",
      params: { nativeStackScreen: "BubbleTap" },
    };
  }

  if (MEDENSPIELE_TRAINING_RE.test(normalized)) {
    return {
      governanceKey: "medenspiele",
      params: { nativeStackScreen: "MedenspielePlanning" },
    };
  }

  if (CLUBHUB_ACCESS_REQUESTS_ADMIN_RE.test(normalized)) {
    return {
      governanceKey: "clubhub",
      params: { nativeStackScreen: "ClubHubUsermanagement" },
    };
  }

  const planung = __resolveTrainingsDeskPlanung(webPath);
  if (planung) return planung;

  const entry = resolvePlatformAppByWebPath(normalized);
  if (!entry || entry.mobileDelivery !== "native" || !entry.nativeScreen) {
    return null;
  }

  const base = entry.webPath.replace(/\/+$/, "");
  const hubBase = entry.hubPath.replace(/\/+$/, "");
  if (normalized !== base && normalized !== hubBase) return null;

  const params =
    entry.governanceKey === "trainerstunden"
      ? { initialTab: "hub" }
      : undefined;

  return {
    governanceKey: entry.governanceKey,
    ...(params ? { params } : {}),
  };
}
